import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Modal, Input, Select, Toggle, StatusBadge, Empty } from "@/components/admin/ui";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";

const EMPTY = { title: "", subtitle: "", image: "", mobileImage: "", ctaText: "", ctaLink: "", placement: "hero", order: 0, active: true };

export default function AdminBanners() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const deskRef = useRef(null); const mobRef = useRef(null);
  const [uploading, setUploading] = useState("");

  const upload = async (file, field) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Only JPG/PNG allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
    setUploading(field);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("productId", "banner");
      const { data } = await api.post("/admin/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, [field]: data.url })); toast.success("Image uploaded");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(""); }
  };

  const load = () => api.get("/admin/banners").then(({ data }) => setItems(data.banners));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/banners/${editId}`, f); else await api.post("/admin/banners", f);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete banner?")) return; await api.delete(`/admin/banners/${id}`); load(); };

  return (
    <div>
      <AdminHeader title="Banners" subtitle="Hero & promotional banners" action={<Btn variant="accent" onClick={() => { setF(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-banner-btn"><Plus className="h-4 w-4" /> Add Banner</Btn>} />
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((b) => (
          <Card key={b.id} className="overflow-hidden" data-testid={`banner-${b.id}`}>
            <div className="h-32 bg-slate-100">{b.image && <img src={resolveMediaUrl(b.image)} alt="" onError={handleMediaError} className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase text-amber-600">{b.placement}</span>
                <StatusBadge status={b.active ? "active" : "inactive"} />
              </div>
              <p className="font-semibold text-slate-900 mt-1">{b.title}</p>
              <p className="text-xs text-slate-500 line-clamp-1">{b.subtitle}</p>
              <div className="flex justify-end gap-1 mt-2">
                <button onClick={() => { setF({ ...b }); setEditId(b.id); setOpen(true); }} className="p-2 text-slate-500 hover:text-amber-600"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(b.id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Empty />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Banner" : "Add Banner"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} testid="banner-title" />
          <Input label="Subtitle" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} />
          <div>
            <label className="text-sm font-medium text-slate-700">Desktop Image</label>
            <input ref={deskRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files?.[0], "image")} data-testid="banner-upload-image" />
            <div className="flex items-center gap-3 mt-1">
              {f.image && <img src={resolveMediaUrl(f.image)} alt="" onError={handleMediaError} className="h-14 w-24 rounded object-cover bg-slate-100" />}
              <Btn type="button" variant="outline" onClick={() => deskRef.current?.click()} loading={uploading === "image"}><Upload className="h-4 w-4" /> Upload</Btn>
              {f.image && <button type="button" onClick={() => setF({ ...f, image: "" })} className="text-xs text-red-500">Remove</button>}
            </div>
            <Input label="…or paste Image URL" value={f.image || ""} onChange={(e) => setF({ ...f, image: e.target.value })} testid="banner-image" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Mobile Image (optional)</label>
            <input ref={mobRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files?.[0], "mobileImage")} data-testid="banner-upload-mobile" />
            <div className="flex items-center gap-3 mt-1">
              {f.mobileImage && <img src={resolveMediaUrl(f.mobileImage)} alt="" onError={handleMediaError} className="h-14 w-24 rounded object-cover bg-slate-100" />}
              <Btn type="button" variant="outline" onClick={() => mobRef.current?.click()} loading={uploading === "mobileImage"}><Upload className="h-4 w-4" /> Upload</Btn>
              {f.mobileImage && <button type="button" onClick={() => setF({ ...f, mobileImage: "" })} className="text-xs text-red-500">Remove</button>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="CTA Text" value={f.ctaText || ""} onChange={(e) => setF({ ...f, ctaText: e.target.value })} />
            <Input label="CTA Link" value={f.ctaLink || ""} onChange={(e) => setF({ ...f, ctaLink: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Placement" value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })}>
              <option value="hero">Hero</option>
              <option value="promo">Promo</option>
            </Select>
            <Input label="Order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} />
          </div>
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
          <Btn variant="accent" type="submit" className="w-full" data-testid="save-banner-btn">Save</Btn>
        </form>
      </Modal>
    </div>
  );
}
